import type { FormlyFieldConfig } from '@ngx-formly/core';
import type { FieldOption } from 'app/features/admin/modules/features/form-builder/form.model';
import type { SourceField } from 'app/features/admin/modules/modules.model';
import {
  directParent,
  findSourceLinks,
  isInsideRepeat,
  isListedInReveal,
  revealHostCandidates,
  revealRequiredWarning,
  siblingOptions,
} from './create-form-builder.util';

/** What the editors need from the form builder component that hosts them. */
export interface CreateFormEditorHost {
  /** The field type selected for the item being edited (`input`, `section`, ...). */
  selectedType: (field: FormlyFieldConfig) => string | undefined;
  /** The builder's whole source tree, for looking up a field's parent. */
  tree: () => SourceField[] | undefined;
  /** Fields of the form being edited that can feed a date. Values keep `entry.`; findDateSource strips it. */
  dateFieldOptions: () => FieldOption[];
}

type RevealList = 'departureRevealFields' | 'destinationRevealFields' | 'footerRevealFields' | 'stopRevealFields';

const EMPTY_DEPARTURE = 'Geef eerst velden de positie "Bij het vertrek".';
const EMPTY_DESTINATION = 'Geef eerst velden de positie "Bij de bestemming".';
const EMPTY_FOOTER = 'Geef eerst velden de positie "Voet van de route".';
const EMPTY_STOP = 'De route heeft nog geen stopvelden.';

const REVEAL_DESCRIPTION =
  'Deze velden staan achter een "Toevoegen"-knop, tot ze een waarde hebben of de gebruiker ze kiest. Kies alleen optionele velden.';

/**
 * Builder editors for the order create form v2 props (docs/superpowers/specs/2026-10-24-order-create-form-v2-design.md §7a).
 * Each one sets a flat SourceField prop that form-generate.util.mjs turns into a field prop.
 */
export function createFormEditorFields(host: CreateFormEditorHost): FormlyFieldConfig[] {
  const links = (field: FormlyFieldConfig) => findSourceLinks(host.tree(), field.model as SourceField);
  const parentOf = (field: FormlyFieldConfig) => directParent(links(field))?.parent;
  const isType = (field: FormlyFieldConfig, type: string) => host.selectedType(field) === type;

  const inRouteSection = (field: FormlyFieldConfig) => {
    const parent = parentOf(field);
    return parent?.type === 'section' && parent.sectionLayout === 'route';
  };
  const inRouteStop = (field: FormlyFieldConfig) => parentOf(field)?.type === 'route';
  const listedInReveal = (field: FormlyFieldConfig) => isListedInReveal(parentOf(field), field.model as SourceField);

  /**
   * A multi-select of the optional fields of one slot, plus a separate warning element (§5.4) when
   * a required field is picked.
   */
  const revealList = (
    key: RevealList,
    label: string,
    candidates: (model: SourceField) => SourceField[] | undefined,
    emptyHint: string,
    visible: (field: FormlyFieldConfig) => boolean
  ): FormlyFieldConfig[] => [
    {
      key,
      type: 'custom-select',
      className: 'w-max-sm',
      props: { label, multiple: true, description: REVEAL_DESCRIPTION },
      expressions: {
        hide: (field: FormlyFieldConfig) => !visible(field),
        'props.options': (field: FormlyFieldConfig) => siblingOptions(candidates(field.model as SourceField)),
        'props.description': (field: FormlyFieldConfig) =>
          candidates(field.model as SourceField)?.length ? REVEAL_DESCRIPTION : emptyHint,
      },
    },
    {
      type: 'builder-warning',
      className: 'w-max-sm',
      expressions: {
        hide: (field: FormlyFieldConfig) => !visible(field),
        'props.text': (field: FormlyFieldConfig) => {
          const model = field.model as SourceField;
          return revealRequiredWarning(candidates(model), model[key]) ?? '';
        },
      },
    },
  ];

  const inSlot = (slot: 'departure' | 'destination' | 'footer') => (model: SourceField) =>
    model.sectionFields?.filter(child => child.routeSlot === slot);

  return [
    {
      key: 'sectionLayout',
      type: 'select',
      defaultValue: '',
      props: {
        label: 'Indeling',
        description:
          'Route: velden staan op een lijn, met vertrek bovenaan, stops in het midden en bestemming onderaan. Geef daarna elk veld een positie.',
        options: [
          { value: '', label: 'Kaart (standaard)' },
          { value: 'route', label: 'Route' },
        ],
      },
      expressions: { hide: (field: FormlyFieldConfig) => !isType(field, 'section') },
    },
    {
      key: 'cardStyle',
      type: 'select',
      defaultValue: '',
      props: {
        label: 'Kaartstijl',
        description:
          'Standaard: kader met scheidingslijn onder de kop. Zonder rand: geen kader en geen lijn, voor kaarten onder een losse kop.',
        options: [
          { value: '', label: 'Standaard' },
          { value: 'plain', label: 'Zonder rand' },
        ],
      },
      expressions: { hide: (field: FormlyFieldConfig) => !isType(field, 'section') },
    },
    ...revealList(
      'departureRevealFields',
      'Vertrek: velden achter "Toevoegen"',
      inSlot('departure'),
      EMPTY_DEPARTURE,
      field => isType(field, 'section') && (field.model as SourceField).sectionLayout === 'route'
    ),
    ...revealList(
      'destinationRevealFields',
      'Bestemming: velden achter "Toevoegen"',
      inSlot('destination'),
      EMPTY_DESTINATION,
      field => isType(field, 'section') && (field.model as SourceField).sectionLayout === 'route'
    ),
    ...revealList(
      'footerRevealFields',
      'Voet: velden achter een "+"-link',
      inSlot('footer'),
      EMPTY_FOOTER,
      field => isType(field, 'section') && (field.model as SourceField).sectionLayout === 'route'
    ),
    {
      key: 'routeSlot',
      type: 'select',
      defaultValue: '',
      resetOnHide: false,
      props: {
        label: 'Positie in de route',
        description: 'Waar dit veld op de routelijn staat. Zonder keuze staat het onder de route.',
        options: [
          { value: '', label: 'Onder de route' },
          { value: 'departure', label: 'Bij het vertrek' },
          { value: 'destination', label: 'Bij de bestemming' },
          { value: 'footer', label: 'Voet van de route' },
        ],
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => !inRouteSection(field) || isType(field, 'route'),
      },
    },
    {
      key: 'stopStyle',
      type: 'select',
      defaultValue: '',
      props: {
        label: 'Weergave van stops',
        description: 'Kaart: elke stop is een uitklapbare kaart met kop en voet. Rij: stops staan in een tijdlijn.',
        options: [
          { value: '', label: 'Rij (standaard)' },
          { value: 'card', label: 'Kaart' },
        ],
      },
      expressions: { hide: (field: FormlyFieldConfig) => !isType(field, 'route') },
    },
    ...revealList(
      'stopRevealFields',
      'Stop: velden achter "Toevoegen"',
      model => model.repeatFields,
      EMPTY_STOP,
      field => isType(field, 'route')
    ),
    {
      key: 'stopSlot',
      type: 'select',
      defaultValue: '',
      resetOnHide: false,
      props: {
        label: 'Positie in de stop',
        description: 'Alleen bij de weergave "Kaart": een veld in de voet staat onderaan de stopkaart.',
        options: [
          { value: '', label: 'In de stop' },
          { value: 'footer', label: 'In de voet van de stopkaart' },
        ],
      },
      expressions: { hide: (field: FormlyFieldConfig) => !inRouteStop(field) },
    },
    {
      key: 'revealIcon',
      type: 'input',
      resetOnHide: false,
      props: {
        label: 'Icoon in het "Toevoegen"-menu',
        description: 'Naam van een icoon, bijvoorbeeld "identification". Leeg geeft een plusje.',
        placeholder: 'identification',
      },
      expressions: { hide: (field: FormlyFieldConfig) => !listedInReveal(field) },
    },
    {
      key: 'revealWith',
      type: 'select',
      defaultValue: '',
      resetOnHide: false,
      props: {
        label: 'Staat in het paneel van',
        description:
          'Het veld krijgt geen eigen menu-item en staat in het paneel van het gekozen veld. Annuleren en Opslaan gelden voor beide.',
        options: [],
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => !listedInReveal(field) || (field.model as SourceField | undefined)?.type === 'repeat',
        'props.options': (field: FormlyFieldConfig) => [
          { label: 'Eigen menu-item', value: '' },
          ...siblingOptions(revealHostCandidates(parentOf(field), field.model as SourceField)),
        ],
      },
    },
    {
      key: 'dateFromField',
      type: 'select',
      defaultValue: '',
      // Not offered inside a repeat; a value set there (dd-api) stays visible so it can be cleared.
      resetOnHide: false,
      props: {
        label: 'Datum uit veld',
        description:
          'Toont alleen een tijd en neemt de dag over uit het gekozen datumveld. Dat datumveld moet verplicht zijn.',
        options: [],
      },
      expressions: {
        hide: (field: FormlyFieldConfig) =>
          !isType(field, 'datetime') || (isInsideRepeat(links(field)) && !(field.model as SourceField).dateFromField),
        'props.options': () => [
          { label: 'Geen', value: '' },
          ...host.dateFieldOptions().filter(option => ['datetime', 'date', 'datepicker_custom'].includes(option.type)),
        ],
      },
    },
    {
      type: 'builder-warning',
      className: 'w-max-sm',
      expressions: {
        hide: (field: FormlyFieldConfig) => !isType(field, 'datetime') || !isInsideRepeat(links(field)),
        'props.text': (field: FormlyFieldConfig) =>
          (field.model as SourceField).dateFromField
            ? "Datum uit veld werkt niet in een herhalend veld: elke rij gebruikt de datum van de eerste rij. Kies 'Geen'."
            : '',
      },
    },
    {
      key: 'cronStyle',
      type: 'select',
      defaultValue: '',
      props: {
        label: 'Herhaalstijl',
        description: 'Wekelijks: weekdagen aanklikken en een startdatum kiezen. Volledig: alle herhaalopties.',
        options: [
          { value: '', label: 'Volledig (standaard)' },
          { value: 'weekly', label: 'Wekelijks' },
        ],
      },
      expressions: { hide: (field: FormlyFieldConfig) => !isType(field, 'cron') },
    },
    {
      key: 'toggleStyle',
      type: 'select',
      defaultValue: '',
      props: {
        label: 'Weergave',
        description: 'Vinkje: één vierkant aanvinkvakje. De opgeslagen waarde blijft hetzelfde.',
        options: [
          { value: '', label: 'Schakelaar (standaard)' },
          { value: 'checkbox', label: 'Vinkje' },
        ],
      },
      expressions: { hide: (field: FormlyFieldConfig) => !isType(field, 'custom-toggle') },
    },
  ];
}
